import React, { useEffect, useMemo, useState } from 'react';
import vaultIcon from '@/assets/icons/vault-dark.svg';
import binIcon from '@/assets/icons/bin.svg';
import editIcon from '@/assets/icons/edit.svg';
import infoIcon from '@/assets/icons/info.svg';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import invoiceWhiteIcon from '@/assets/icons/invoice-white.svg';
import workActivityIcon from '@/assets/icons/work-activity-primary.svg';
import workActivityWhiteIcon from '@/assets/icons/work-activity-white.svg';
import { Button } from '@/components/ui/button';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import noWorkActivityIcon from '@/assets/icons/no-work-activity.svg';
import { PAGE } from '@/routes';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { bankAccountService } from '@/services/bank-accounts.service';
import { BankAccount } from '@/services/types/bank-accounts.types';
import InfoModal from '@/components/molecules/InfoModal';
import deleteBankAccountIcon from '@/assets/icons/delete-bankAccount.svg';
import toast from 'react-hot-toast';
import ExplanatoryModal from '@/components/molecules/ExplanatoryModal';

import baExpanatoryImg from '@/assets/images/ba-explanatory-1.png';
import baExpanatoryImg2 from '@/assets/images/ba-explanatory-2.png';
import { Invoice } from '@/services/types/invoice.types';
import { invoiceService } from '@/services/invoice.service';
import { workActivityService } from '@/services/work-activity.service';
import { withPaymentAmountFromAllocationKey } from '@/utils/invoiceAllocationAmount';
import TabPills from '@/components/molecules/TabPills';
import InvoicesTable from '@/components/molecules/invoice/InvoicesTable';
import WorkActivityTable from '@/components/molecules/workActivity/WorkActivityTable';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import GeneralInfoAccordion from '@/components/molecules/GeneralInfoAccordion';
import { ReactComponent as InvoiceUploadIcon } from '@/assets/icons/upload-invoice.svg';
import { WorkActivity } from '@/services/types/work-activity.types';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';

type ContentTab = 'invoice' | 'work-activity';

const steps = [
  {
    title: 'What Is a Bank Account?',
    message:
      'Bank accounts act as the primary source for payments and deposits, seamlessly integrating with your budget.',
    img: baExpanatoryImg
  },
  {
    title: 'Tracking Invoices in Your Bank Account',
    message:
      'All invoices are linked to bank accounts, enabling tracking of cash inflows and outflows while maintaining account balances.',
    img: baExpanatoryImg2
  }
];

const BankAccountDetailPage = () => {
  const [bankAccountMode, setBankAccountMode] = useState('euro');
  const [bankAccount, setBankAccount] = useState<BankAccount>();
  const [loadingBankAccount, setLoadingBankAccount] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [isExplanatoryOpen, setIsExplanatoryOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [activeInvoiceTab, setActiveInvoiceTab] = useState('All');
  const [activeContentTab, setActiveContentTab] = useState<ContentTab>('invoice');
  const [workActivities, setWorkActivities] = useState<WorkActivity[]>([]);
  const [loadingWorkActivities, setLoadingWorkActivities] = useState(false);
  const [totalWorkActivityCount, setTotalWorkActivityCount] = useState(0);

  const { budgetId = '', bankAccountId = '' } = useParams();

  const location = useLocation();
  const navigate = useNavigate();
  const budget = location.state?.budget as any | undefined;
  const { can } = useBudgetPermissions({
    budgetId,
    fallbackMembers: budget?.members ?? []
  });
  const canCreateInvoice = can('invoice:create');
  const canEditBankAccount = can('bankAccount:edit');
  const canDeleteBankAccount = can('bankAccount:delete') && !bankAccount?.is_default;

  const generalInfoItems = useMemo(
    () => [
      { label: 'Name of the Bank', value: bankAccount?.bankName || '-' },
      { label: 'Country of the Bank', value: bankAccount?.bankCountry || '-' },
      { label: 'Bank Account Number', value: bankAccount?.accountNumber || '-' },
      {
        label: 'Initial Amount',
        value: bankAccount?.initialAmount != null ? `€${bankAccount.initialAmount.toLocaleString()}` : '-',
        description: budget?.bmev != null ? '100% of BMEV' : undefined
      }
    ],
    [
      bankAccount?.bankName,
      bankAccount?.bankCountry,
      bankAccount?.accountNumber,
      bankAccount?.initialAmount,
      budget?.bmev
    ]
  );

  const handleInvoiceSelect = (invoice: Invoice & { budget?: any }) => {
    if (budgetId) {
      navigate(`${PAGE.BUDGETS.path}/${budgetId}/invoices/${invoice._id}`, {
        state: { budget, from: 'bankAccount', bankAccount }
      });
    }
  };

  useEffect(() => {
    fetchBankAccountDetail();
  }, [budgetId, bankAccountId, location.pathname]);

  // Force browser back navigation (and any history "pop") from this page to land on
  // the parent Budget Detail page. We push a sentinel history entry on mount so the
  // first back press triggers `popstate`, which we intercept to redirect.
  useEffect(() => {
    if (!budgetId) return;

    window.history.pushState({ __bankAccountDetailSentinel: true }, '', window.location.href);

    const handlePopState = () => {
      navigate(`/budgets/${budgetId}`, { replace: true });
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [budgetId, navigate]);

  const handleBack = () => {
    navigate(`/budgets/${budgetId}`);
  };

  useEffect(() => {
    fetchBankAccountInvoices();
  }, [activeInvoiceTab, budgetId, bankAccountId]);

  useEffect(() => {
    fetchBankAccountWorkActivities();
  }, [budgetId, bankAccountId]);

  const fetchBankAccountWorkActivities = async () => {
    if (!budgetId || !bankAccountId) return;
    try {
      setLoadingWorkActivities(true);
      const response = await workActivityService.getWorkActivities(budgetId, {
        type: 'all',
        page: 1,
        limit: 50
      });
      const filtered = (Array.isArray(response.data) ? response.data : []).filter((activity) =>
        activity.bank_allocations?.some((allocation) => allocation.bank_account_id === bankAccountId)
      );
      setWorkActivities(filtered);
      setTotalWorkActivityCount(filtered.length);
    } catch (error) {
      console.error('Error fetching bank account work activities:', error);
      setWorkActivities([]);
      setTotalWorkActivityCount(0);
    } finally {
      setLoadingWorkActivities(false);
    }
  };

  const fetchBankAccountDetail = async () => {
    try {
      setLoadingBankAccount(true);
      const { data: bankAccountData } = await bankAccountService.getBankAccount(budgetId, bankAccountId);
      setBankAccount(bankAccountData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBankAccount(false);
    }
  };

  const fetchBankAccountInvoices = async () => {
    if (budgetId && bankAccountId) {
      try {
        const { data: invoicesData } = await invoiceService.getInvoices({
          page: 1,
          limit: 12,
          budget_id: budgetId,
          invoice_category: activeInvoiceTab === 'All' ? undefined : activeInvoiceTab.toUpperCase(),
          type: undefined,
          bank_account_id: bankAccountId
        });
        const filteredInvoices =
          invoicesData.data && invoicesData.data.length > 0
            ? invoicesData.data
                .filter((invoice: Invoice) => invoice.is_draft === false)
                .map((invoice: Invoice) =>
                  withPaymentAmountFromAllocationKey(invoice, bankAccountId, 'allocation_bank_account')
                )
            : [];
        setInvoices(filteredInvoices);
      } catch (error) {
        console.error('Error fetching budget invoices:', error);
      } finally {
        setLoadingInvoices(false);
      }
    }
  };

  const handleEditClick = () => {
    if (budgetId) {
      navigate(`/budgets/${budgetId}/bank-accounts/${bankAccountId}/edit`);
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmModal(true);
  };

  const handleDeleteBankAccount = async () => {
    await bankAccountService
      .deleteBankAccount(budgetId, bankAccountId)
      .then(() => {
        toast.success('Budget deleted successfully');
        navigate(`/budgets/${budgetId}/bank-accounts`);
      })
      .catch((error) => {
        console.error('Error deleting bank account:', error);
        toast.error('Failed to delete bank account');
      });
  };

  return (
    <>
      <MobileAppBar
        title="Bank Account"
        onBack={handleBack}
        rightTwoIcon={canDeleteBankAccount ? binIcon : undefined}
        rightIcon={infoIcon}
        onRightTwoIconClick={canDeleteBankAccount ? handleDeleteClick : undefined}
        onRightIconClick={() => {
          setCurrentStep(0);
          setIsExplanatoryOpen(true);
        }}
        rightIconClassName="w-[24px] h-[24px]"
      />
      <div className="h-full flex flex-col items-stretch gap-4 p-4 md:p-0 relative">
        {/* BANK INFORMATION - MOBILE */}
        <div className="flex items-center justify-between gap-5 mb-6 md:hidden">
          <div className="flex items-center gap-3">
            <img src={vaultIcon} alt="vault-icon" width={40} height={40} />
            <div>
              <h4 className="font-bold text-base">{bankAccount?.accountName || '-'}</h4>
            </div>
          </div>

          {canEditBankAccount && (
            <button type="button" onClick={handleEditClick}>
              <img src={editIcon} alt="edit-icon" width={24} height={24} />
            </button>
          )}
        </div>

        {/* BALANCE(WB + MB) AND GENERAL INFORMATION - WEB */}
        <div className="flex items-stretch gap-4">
          <div className="flex-[2] hidden md:block border bg-white rounded-[16px] p-4">
            <header className="flex items-center justify-between gap-4 mb-4">
              <h3 className="flex items-center gap-[0.6rem]">
                {' '}
                <img src={vaultIcon} alt="vault icon" />{' '}
                <span className="font-semibold text-2xl"> {bankAccount?.accountName || '-'}</span>
              </h3>
              <div className="flex items-center gap-3">
                {canEditBankAccount && (
                  <button onClick={handleEditClick} title="Edit">
                    <img className="w-[28px] h-[28px]" src={editIcon} alt="vault icon" />
                  </button>
                )}
                <button
                  title="Info"
                  onClick={() => {
                    setCurrentStep(0);
                    setIsExplanatoryOpen(true);
                  }}
                >
                  <img className="w-[28px] h-[28px]" src={infoIcon} alt="vault icon" />
                </button>
                {canDeleteBankAccount && (
                  <button onClick={handleDeleteClick} title="Delete">
                    <img className="w-[28px] h-[28px]" src={binIcon} alt="vault icon" />
                  </button>
                )}
              </div>
            </header>
            <div className="bg-lightPurple p-4 rounded-[16px] border border-primary min-h-[170px]">
              <p className="font-bold text-primary mb-3">General Information</p>
              {loadingBankAccount ? (
                <span className="block w-full text-secondary text-center py-8">Loading...</span>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <p className="text-sm text-secondary">Name of the bank</p>
                      <span className="font-medium">{bankAccount?.bankName || '-'}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-secondary">Country of the bank</p>
                      <span className="font-medium">{bankAccount?.bankCountry || '-'}</span>
                    </div>
                  </div>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-secondary">Bank Account Number</p>
                      <span className="font-medium">{bankAccount?.accountNumber || '-'}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-secondary">Initial Amount</p>
                      <span className="font-medium">€{bankAccount?.initialAmount || '-'}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex-1 w-full flex flex-col items-stretch md:min-w-[418px] border bg-white rounded-[16px] p-4">
            <header className="flex items-center justify-between">
              <div>
                <p className="font-medium text-primary text-sm md:text-base md:text-foreground md:font-bold">Balance</p>
                <p className="md:hidden text-primary text-[1.25rem] font-bold leading-[135%]">
                  {loadingBankAccount
                    ? '...'
                    : bankAccountMode === 'euro'
                      ? `€${bankAccount?.remainingBalance || 'N/A'}`
                      : `${
                          bankAccount?.initialAmount
                            ? Math.round(((bankAccount?.remainingBalance || 0) / bankAccount.initialAmount) * 100)
                            : 'N/A'
                        }%`}
                </p>
              </div>
              <Tabs defaultValue="percentage" value={bankAccountMode} onValueChange={setBankAccountMode}>
                <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                  <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                    <img
                      src={percentageIcon}
                      alt="percentage-icon"
                      width={13}
                      height={17}
                      className="group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                  <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                    <img
                      src={euroIcon}
                      alt="euro-icon"
                      width={12}
                      height={12}
                      className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </header>
            <div className="flex-1 flex flex-col items-start md:items-center justify-center py-2 md:py-8">
              <p className="hidden md:block text-primary text-[3rem] font-bold leading-[135%]">
                {loadingBankAccount
                  ? '...'
                  : bankAccountMode === 'euro'
                    ? `€${bankAccount?.remainingBalance || 'N/A'}`
                    : `${
                        bankAccount?.initialAmount
                          ? Math.round(((bankAccount?.remainingBalance || 0) / bankAccount.initialAmount) * 100)
                          : 'N/A'
                      }%`}
              </p>
              <span className="text-secondary text-xs md:text-[1.25rem] ">
                {loadingBankAccount
                  ? '...'
                  : bankAccountMode === 'euro'
                    ? `${((bankAccount?.remainingBalance || 0) - (bankAccount?.initialAmount || 0)).toLocaleString('en-US')}€ from Initial amount`
                    : `${
                        bankAccount?.initialAmount
                          ? Math.round(
                              (((bankAccount?.remainingBalance || 0) - bankAccount.initialAmount) /
                                bankAccount.initialAmount) *
                                100
                            )
                          : 0
                      }% from Initial amount`}
              </span>
            </div>
          </div>
        </div>

        {/* GENERAL INFORMATION - MOBILE */}
        <div className="md:hidden">
          {loadingBankAccount ? (
            <span className="block w-full text-secondary text-center py-8">Loading...</span>
          ) : (
            <GeneralInfoAccordion items={generalInfoItems} />
          )}
        </div>

        {/* Invoice / Work Activity */}
        <section className="mt-3 md:mt-0 md:flex-1 md:bg-white md:p-4 md:rounded-[16px] md:border md:border-lightGray md:flex md:flex-col">
          <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <Tabs
              value={activeContentTab}
              onValueChange={(value) => setActiveContentTab(value as ContentTab)}
              className="w-full md:w-auto"
            >
              <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem] w-full grid grid-cols-2 md:inline-flex md:w-auto">
                <TabsTrigger value="invoice" className="group py-[0.3rem] px-3 w-full">
                  <h4 className="flex items-center justify-center md:justify-start gap-2.5">
                    <img src={invoiceIcon} alt="invoice-icon" className="group-data-[state=active]:hidden" />
                    <img src={invoiceWhiteIcon} alt="invoice-icon" className="hidden group-data-[state=active]:block" />
                    <span className="font-bold text-primary group-data-[state=active]:text-white">Recent Invoice</span>
                  </h4>
                </TabsTrigger>
                <TabsTrigger value="work-activity" className="group py-[0.3rem] px-3 w-full">
                  <h4 className="flex items-center justify-center md:justify-start gap-2.5">
                    <img src={workActivityIcon} alt="work-activity-icon" className="group-data-[state=active]:hidden" />
                    <img
                      src={workActivityWhiteIcon}
                      alt="work-activity-icon"
                      className="hidden group-data-[state=active]:block"
                    />
                    <span className="font-bold text-primary group-data-[state=active]:text-white">Work Activity</span>
                  </h4>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {activeContentTab === 'invoice' && invoices.length > 0 && (
              <Button
                variant="text"
                onClick={() => navigate(`/budgets/${budgetId}/invoices`)}
                className="hidden md:inline-flex py-0 h-[30px] shrink-0"
              >
                See All
              </Button>
            )}
            {activeContentTab === 'work-activity' && totalWorkActivityCount > 7 && (
              <Button
                variant="text"
                onClick={() => navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity`)}
                className="hidden md:inline-flex py-0 h-[30px] shrink-0"
              >
                See All
              </Button>
            )}
          </header>

          {activeContentTab === 'invoice' &&
            (invoices.length > 0 || activeInvoiceTab !== 'All' ? (
              <div className="flex flex-col gap-2 bg-lightPurple rounded-[16px] p-4 py-5 mt-4 md:mt-3 md:p-0 md:bg-white">
                <TabPills
                  options={['All', 'Expense', 'Revenue']}
                  value={activeInvoiceTab}
                  onValueChange={setActiveInvoiceTab}
                />

                <InvoicesTable
                  invoices={invoices.slice(0, 10)}
                  setSelectedInvoice={() => {}}
                  onInvoiceSelect={handleInvoiceSelect}
                />
              </div>
            ) : (
              <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
                <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                  <img src={noInvoiceIcon} alt="no invoice icon" className="md:w-[40px] md:h-[40px]" />
                  <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                    <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                      There are no Invoice to show.
                    </span>{' '}
                    <span className="md:text-secondary md:text-base md:font-medium">
                      Please create a new invoice to allocate budget!
                    </span>
                  </span>
                </div>
                {canCreateInvoice && (
                  <Button
                    onClick={() =>
                      navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
                        state: { from: 'budget', budgetId }
                      })
                    }
                    variant="primary-reverse"
                    className="rounded-full font-semibold px-5 text-base"
                  >
                    Create an Invoice
                  </Button>
                )}
              </div>
            ))}

          {activeContentTab === 'work-activity' &&
            (workActivities.length > 0 || loadingWorkActivities ? (
              <div className="flex flex-col gap-2 min-w-0 bg-lightPurple rounded-[16px] p-4 py-5 mt-4 md:mt-3 md:p-0 md:bg-white">
                <WorkActivityTable
                  workActivities={workActivities.slice(0, 7)}
                  loading={loadingWorkActivities}
                  budgetId={budgetId}
                  onWorkActivitySelect={(wa) => {
                    if (budgetId) {
                      navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/${wa._id}`);
                    }
                  }}
                />
              </div>
            ) : (
              <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
                <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                  <img src={noWorkActivityIcon} alt="no work activity icon" className="md:w-[40px] md:h-[40px]" />
                  <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                    <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                      There are no work activity to show.
                    </span>{' '}
                    <span className="md:text-secondary md:text-base md:font-medium">
                      You can create work activity to request a payment
                    </span>
                  </span>
                </div>
                <Button
                  onClick={() =>
                    navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/create`, {
                      state: { from: 'budget', budgetId }
                    })
                  }
                  variant="primary-reverse"
                  className="rounded-full font-semibold px-5 text-base"
                >
                  Create a Work Activity
                </Button>
              </div>
            ))}
        </section>

        {canCreateInvoice && (
          <div className="rounded-[16px] md:rounded-[22px] flex-1 flex items-center justify-center fixed md:absolute bottom-[26px] right-[26px] md:right-[36px] shadow-[0px_4px_4px_0px_#00000040]">
            <Button
              onClick={() => {
                navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
                  state: { from: 'budget', budgetId }
                });
              }}
              className="px-3 w-[48px] h-[48px] md:w-[64px] md:h-[64px] rounded-[16px] md:rounded-[22px]"
            >
              <InvoiceUploadIcon className="w-8 h-8" width={42} height={42} />
            </Button>
          </div>
        )}

        {/* Delete Confirm Modal */}
        {deleteConfirmModal && (
          <InfoModal
            isOpen={deleteConfirmModal}
            variant="confirm"
            title="Are you sure want to Delete this Bank Account?"
            message={<p>Deleted bank account will removed permanently and unable to restore</p>}
            buttonText="Delete"
            onButtonClick={handleDeleteBankAccount}
            onClose={() => setDeleteConfirmModal(false)}
            icon={deleteBankAccountIcon}
          />
        )}

        {/* Explanatory Modal */}
        {isExplanatoryOpen && (
          <ExplanatoryModal
            isOpen={isExplanatoryOpen}
            steps={steps}
            currentStep={currentStep}
            buttonText="Next"
            onButtonClick={() => {
              setCurrentStep(currentStep + 1);
            }}
            onClose={() => {
              setIsExplanatoryOpen(false);
            }}
            onStepChange={setCurrentStep}
          />
        )}
      </div>
    </>
  );
};

export default BankAccountDetailPage;
